import { useEffect, useState, useCallback } from 'react';
import { fetchMissingActivityKPI } from '../api/missingActivityApi';

export const useMissingActivityKpi = (activeFile = null, options = {}) => {
  const { batchData } = options;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Consolidated data loading logic
  const loadData = useCallback(async (fileType = null) => {
    setLoading(true);
    setError(null);
    try {
      const effectiveFileType = fileType ?? activeFile ?? null;

      // Check if we have batch data available (preferred for performance)
      if (batchData && typeof batchData === 'object' && batchData.missing_activities) {
        console.info('useMissingActivityKpi: Using batch data for missing activities KPI');
        setData(batchData.missing_activities);
        return;
      }

      // Fallback to individual API call if no batch data available
      console.info('useMissingActivityKpi: Falling back to individual API call');
      const kpi = await fetchMissingActivityKPI(effectiveFileType);
      setData(kpi);
    } catch (e) {
      setError(e?.message || 'Failed to load KPI');
    } finally {
      setLoading(false);
    }
  }, [activeFile, batchData]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      // Priority: use batch data if available
      if (batchData && typeof batchData === 'object' && batchData.missing_activities) {
        console.info('useMissingActivityKpi: Using batch data for missing activities KPI');
        setLoading(false);
        if (!cancelled) setData(batchData.missing_activities);
        return;
      }

      // If batch data exists but doesn't have missing_activities yet, wait for it (batch might still be loading)
      if (batchData && typeof batchData === 'object' && batchData.missing_activities === undefined) {
        console.info('useMissingActivityKpi: Batch data exists but missing_activities not ready yet, waiting...');
        // Return without setting loading state - this will retry on next batchData change
        return;
      }

      // Fallback: individual API call only if batch data is not available at all or explicitly null
      console.info('useMissingActivityKpi: Batch data not available, using individual API call');
      setLoading(true);
      setError(null);
      try {
        const kpi = await fetchMissingActivityKPI(activeFile ?? null);
        if (!cancelled) setData(kpi);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load KPI');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => { cancelled = true; };
  }, [activeFile, batchData]);

  return { data, loading, error, refresh: loadData };
};
